// Client layer (TS-2.5.03). The read-only member list a stat card's click opens (decisions.md
// D-284): a card's number IS the length of `StatCardValue.memberProjectKeys` (D-283's one-truth
// enumeration) - this component only RESOLVES those keys against the roadmap's own project data
// (the same `projectsByKey` map RoadmapPage/FrozenRoadmapPanel already build for the board) and
// renders the projects for display. It never re-derives which projects belong to a card; a key
// the current view's project data does not carry (e.g. a filtered-away/archived edge case) is
// named as a count, not silently dropped, so the reader never sees fewer rows than the number the
// card itself printed with no explanation.
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Modal } from "../../components/Modal";
import { resolveColor } from "./lib/colorTokens";
import { readableTextColor } from "./lib/contrast";
import { RiskTypeBadge } from "./RiskTypeBadge";
import type { ProjectView, StatCardValue } from "../../api/generated/contracts";
import { strings } from "../../strings";

const S = strings.roadmap.statCardModal;

export interface StatCardMembersModalProps {
  readonly open: boolean;
  readonly card: StatCardValue;
  readonly projectsByKey: ReadonlyMap<string, ProjectView>;
  readonly onClose: () => void;
}

export function StatCardMembersModal({ open, card, projectsByKey, onClose }: StatCardMembersModalProps) {
  const resolved: ProjectView[] = [];
  for (const key of card.memberProjectKeys) {
    const project = projectsByKey.get(key);
    if (project) resolved.push(project);
  }
  const missingCount = card.memberProjectKeys.length - resolved.length;

  return (
    <Modal open={open} title={card.label} onClose={onClose} closeLabel={S.close}>
      {card.memberProjectKeys.length === 0 ? (
        <p>{S.empty}</p>
      ) : resolved.length > 0 ? (
        <ul className="eg-stat-card-modal__list">
          {resolved.map((project) => (
            <li key={project.key} className="eg-stat-card-modal__item">
              <Link to={`/projects/${project.key}`} className="eg-stat-card-modal__name">
                {project.name}
              </Link>
              <span className="eg-stat-card-modal__meta">{project.team}</span>
              <span
                className="eg-detail-card__pill"
                style={
                  {
                    "--eg-chip-color": resolveColor(project.status.colorHex),
                    "--eg-chip-ink": readableTextColor(project.status.colorHex),
                  } as CSSProperties
                }
              >
                {project.status.canonical}
              </span>
              <span className="eg-stat-card-modal__meta">{project.goLive.label}</span>
              {/* D-285/D-286 enrichment: this member's own open risks/blockers, resolved from
                  the same `ProjectView.risks` the board already carries (`RiskItem.isOpen`) -
                  nothing renders when a project carries none. */}
              {project.risks.filter((risk) => risk.isOpen).length > 0 && (
                <ul className="eg-stat-card-modal__project-risks">
                  {project.risks
                    .filter((risk) => risk.isOpen)
                    .map((risk) => (
                      <li key={risk.key} className="eg-stat-card-modal__project-risk">
                        <RiskTypeBadge note={risk} />
                        <span>{risk.text}</span>
                      </li>
                    ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {missingCount > 0 && <p className="eg-stat-card-modal__missing">{S.missingProjects(missingCount)}</p>}
    </Modal>
  );
}
